import React, { useState } from 'react';
import MarcEtapa from './MarcEtapa';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  XMarkIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import TresAnalisis from './TresAnalisis';

const ProblemSituationStage = ({ data, onUpdate, onNext }) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState(data.problemSituation?.description || '');
  const [stakeholders, setStakeholders] = useState(data.problemSituation?.stakeholders || []);
  const [concerns, setConcerns] = useState(data.problemSituation?.concerns || []);
  const [newStakeholder, setNewStakeholder] = useState('');
  const [newConcern, setNewConcern] = useState('');

  const handleSave = () => {
    onUpdate({
      problemSituation: {
        ...data.problemSituation,
        description,
        stakeholders,
        concerns,
        context: { lastUpdated: new Date().toISOString() }
      }
    });
  };

  const addStakeholder = () => {
    if (newStakeholder.trim()) {
      const newStakeholderObj = {
        id: Date.now().toString(),
        name: newStakeholder.trim(),
        role: '',
        influence: 'medium',
        interest: 'medium'
      };
      setStakeholders([...stakeholders, newStakeholderObj]);
      setNewStakeholder('');
      handleSave();
    }
  };

  const removeStakeholder = (id) => {
    setStakeholders(stakeholders.filter(s => s.id !== id));
  };

  const addConcern = () => {
    if (newConcern.trim()) {
      const newConcernObj = {
        id: Date.now().toString(),
        description: newConcern.trim(),
        priority: 'medium',
        category: 'general'
      };
      setConcerns([...concerns, newConcernObj]);
      setNewConcern('');
      handleSave();
    }
  };

  const removeConcern = (id) => {
    setConcerns(concerns.filter(c => c.id !== id));
  };

  return (
    <MarcEtapa className="p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Introduction */}
        <div className="bg-primary-50 border border-primary-200 rounded-md p-6">
          <h2 className="text-lg font-semibold text-primary-900 mb-2">
            Stage 1: Understanding the Problem Situation
          </h2>
          <p className="text-primary-800 text-sm">
            {t('ssm.s1Why')}
          </p>
        </div>

        {/* Problem Description */}
        <div className="bg-white border border-gray-200 rounded-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('ssm.problemDescription')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('ssm.s1Describe')}
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleSave}
            placeholder={t('ssm.problemPlaceholder')}
            className="w-full h-48 p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Stakeholders */}
        <div className="bg-white border border-gray-200 rounded-md p-6">
          <div className="flex items-center space-x-2 mb-4">
            <UserGroupIcon className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">{t('ssm.stakeholders')}</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            {t('ssm.s1Who')}
          </p>
          
          <div className="space-y-3 mb-4">
            {stakeholders.map((stakeholder) => (
              <div key={stakeholder.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex-1">
                  <input
                    type="text"
                    value={stakeholder.name}
                    onChange={(e) => {
                      const updated = stakeholders.map(s => 
                        s.id === stakeholder.id ? { ...s, name: e.target.value } : s
                      );
                      setStakeholders(updated);
                    }}
                    onBlur={handleSave}
                    className="font-medium text-gray-900 bg-transparent border-none p-0 focus:ring-0"
                    placeholder={t('ssm.stakeholderName')}
                  />
                  <input
                    type="text"
                    value={stakeholder.role}
                    onChange={(e) => {
                      const updated = stakeholders.map(s => 
                        s.id === stakeholder.id ? { ...s, role: e.target.value } : s
                      );
                      setStakeholders(updated);
                    }}
                    onBlur={handleSave}
                    className="text-sm text-gray-600 bg-transparent border-none p-0 focus:ring-0 block w-full"
                    placeholder={t('ssm.stakeholderRole')}
                  />
                </div>
                <button
                  onClick={() => removeStakeholder(stakeholder.id)}
                  className="text-gray-500 hover:text-danger-500"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex space-x-2">
            <input
              type="text"
              value={newStakeholder}
              onChange={(e) => setNewStakeholder(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addStakeholder()}
              placeholder={t('ssm.addStakeholder')}
              className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              onClick={addStakeholder}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center space-x-1"
            >
              <PlusIcon className="h-4 w-4" />
              <span>{t('ssm.afegir')}</span>
            </button>
          </div>
        </div>

        {/* Concerns */}
        <div className="bg-white border border-gray-200 rounded-md p-6">
          <div className="flex items-center space-x-2 mb-4">
            <ExclamationTriangleIcon className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">{t('ssm.concerns')}</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            {t('ssm.s1Concerns')}
          </p>
          
          <div className="space-y-3 mb-4">
            {concerns.map((concern) => (
              <div key={concern.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex-1">
                  <textarea
                    value={concern.description}
                    onChange={(e) => {
                      const updated = concerns.map(c => 
                        c.id === concern.id ? { ...c, description: e.target.value } : c
                      );
                      setConcerns(updated);
                    }}
                    onBlur={handleSave}
                    className="w-full text-gray-900 bg-transparent border-none p-0 focus:ring-0 resize-none"
                    rows="2"
                    placeholder={t('ssm.concernPlaceholder')}
                  />
                  <select
                    value={concern.priority}
                    onChange={(e) => {
                      const updated = concerns.map(c => 
                        c.id === concern.id ? { ...c, priority: e.target.value } : c
                      );
                      setConcerns(updated);
                    }}
                    className="mt-2 text-xs text-gray-600 bg-transparent border-none p-0 focus:ring-0"
                  >
                    <option value="low">{t('ssm.low')}</option>
                    <option value="medium">{t('ssm.mediumP')}</option>
                    <option value="high">{t('ssm.high')}</option>
                  </select>
                </div>
                <button
                  onClick={() => removeConcern(concern.id)}
                  className="text-gray-500 hover:text-danger-500 ml-2"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex space-x-2">
            <input
              type="text"
              value={newConcern}
              onChange={(e) => setNewConcern(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addConcern()}
              placeholder={t('ssm.addConcern')}
              className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              onClick={addConcern}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center space-x-1"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>

        {/* LES TRES ANÀLISIS DE CHECKLAND.
            Van aquí i no a una etapa pròpia perquè són el «finding out»: no són un pas més de la
            seqüència, són tres maneres de mirar-se la mateixa situació mentre s'explora. I el que
            en surt és la font de l'eix «culturalment factible» de l'etapa 6, que fins avui no en
            tenia cap. */}
        <div className="pt-2">
          <h2 className="text-lg font-semibold text-gray-900">{t('ssm.anTitol')}</h2>
          <p className="text-sm text-gray-600 mt-1 mb-4">{t('ssm.anQue')}</p>
          <TresAnalisis
            problemSituation={data.problemSituation}
            onCanvi={(canvis) => onUpdate({
              problemSituation: { ...data.problemSituation, description, stakeholders, concerns, ...canvis },
            })} />
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <div>
            <p className="text-sm text-gray-600">
              {t('ssm.s1Next')}
            </p>
          </div>
          <button
            onClick={onNext}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            {t('ssm.continueTo', { etapa: t('ssm.s2') })} →
          </button>
        </div>
      </div>
    </MarcEtapa>
  );
};

export default ProblemSituationStage;